/**
 * FILE: src/blueprint/hooks/useProgressiveInsights.js
 */

import { useCallback, useMemo, useRef } from 'react';
import ProgressiveAdapter from '../adapters/progressive/progressiveAdapter.js';
import { postRunProgressive } from '../api/clientApi.js';
import { ACTIONS } from '../state/blueprintReducer.js';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function makeAnswerKey(packId, moduleId, trackId, stepId) {
  const p = String(packId || '').trim();
  const m = String(moduleId || '').trim();
  const t = String(trackId || '').trim();
  const s = String(stepId || '').trim();

  return p && m && t && s ? `${p}::${m}::${t}::${s}` : s;
}

function normalizeResponses(answers, steps, packId, moduleId, trackId) {
  const safeAnswers = asObject(answers);
  const safeSteps = asArray(steps);
  const out = {};

  for (const step of safeSteps) {
    const stepId = String(step?.id ?? '').trim();
    if (!stepId) continue;

    const scopedKey = makeAnswerKey(packId, moduleId, trackId, stepId);

    if (Object.prototype.hasOwnProperty.call(safeAnswers, scopedKey)) {
      out[stepId] = safeAnswers[scopedKey];
      continue;
    }

    if (Object.prototype.hasOwnProperty.call(safeAnswers, stepId)) {
      out[stepId] = safeAnswers[stepId];
    }
  }

  return out;
}

function readStepReportMeta(steps, currentStepId) {
  const targetId = String(currentStepId || '').trim();
  if (!targetId) return { reportId: '', reportType: '' };

  for (const step of asArray(steps)) {
    if (String(step?.id ?? '').trim() !== targetId) continue;

    return {
      reportId: String(step?.reportId || '').trim(),
      reportType: String(step?.reportType || '').trim(),
    };
  }

  return { reportId: '', reportType: '' };
}

export function useProgressiveInsights({
  dispatch,
  serverBaseUrl,
  answers,
  packId,
  moduleId,
  trackId,
  steps,
  currentStepId = '',
  assistantId = '',
  typingMode = '',
  enabled = true,
}) {
  const TEMP_ASSISTANT_BY_MODULE = {
    problem: 'scout',
    identity: 'lumi',
  };

  const abortRef = useRef(null);

  const safePackId = String(packId || '').trim();
  const safeModuleId = String(moduleId || '').trim();
  const safeTrackId = String(trackId || '').trim();
  const safeCurrentStepId = String(currentStepId || '').trim();
  const safeAssistantId = String(
    assistantId || TEMP_ASSISTANT_BY_MODULE[safeModuleId] || 'system'
  ).trim();
  const safeTypingMode = String(typingMode || '').trim();

  const safeSteps = useMemo(() => asArray(steps), [steps]);

  const responses = useMemo(() => {
    return normalizeResponses(answers, safeSteps, safePackId, safeModuleId, safeTrackId);
  }, [answers, safeSteps, safePackId, safeModuleId, safeTrackId]);

  const currentStepReportMeta = useMemo(() => {
    return readStepReportMeta(safeSteps, safeCurrentStepId);
  }, [safeSteps, safeCurrentStepId]);

  const runProgressiveInsights = useCallback(
    async (options = {}) => {
      if (!enabled) return null;
      if (!dispatch) return null;
      if (!serverBaseUrl || !safePackId || !safeModuleId || !safeTrackId) return null;
      if (!safeSteps.length) return null;

      const effectiveAnswers = options?.answersOverride || answers;
      const effectiveResponses = normalizeResponses(
        effectiveAnswers,
        safeSteps,
        safePackId,
        safeModuleId,
        safeTrackId
      );

      const responseKeys = Object.keys(effectiveResponses);
      const isAnswerReviewStep =
        currentStepReportMeta.reportId === 'answer_review' ||
        currentStepReportMeta.reportType === 'answer_review';

      if (responseKeys.length === 0 && !isAnswerReviewStep && !safeCurrentStepId) {
        return null;
      }

      abortRef.current?.abort?.();

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        dispatch({ type: ACTIONS.INSIGHTS_LOADING });

        const clientRequestId =
          (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
          `${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const payload = {
          pack_id: safePackId,
          module_id: safeModuleId,
          track: safeTrackId,
          responses: effectiveResponses,
          client_request_id: clientRequestId,
        };

        if (safeCurrentStepId) {
          payload.current_step_id = safeCurrentStepId;
        }

        if (safeAssistantId) {
          payload.assistant = { id: safeAssistantId };
        }

        if (safeTypingMode) {
          payload.typing_mode = safeTypingMode;
        }

        const response = await postRunProgressive({
          serverBaseUrl,
          payload,
          signal: controller.signal,
        });

        const adapted = ProgressiveAdapter(response);

        dispatch({
          type: ACTIONS.INSIGHTS_SUCCESS,
          payload: adapted,
        });

        return adapted;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return null;
        }

        dispatch({
          type: ACTIONS.INSIGHTS_ERROR,
          payload: {
            error: error?.message || 'Failed to load insights.',
          },
        });

        return null;
      }
    },
    [
      dispatch,
      enabled,
      serverBaseUrl,
      safePackId,
      safeModuleId,
      safeTrackId,
      safeCurrentStepId,
      safeAssistantId,
      safeTypingMode,
      safeSteps,
      answers,
      currentStepReportMeta.reportId,
      currentStepReportMeta.reportType,
    ]
  );

  return runProgressiveInsights;
}

export default useProgressiveInsights;
